import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Crown, Mail, Send } from 'lucide-react';
import { WHATSAPP_CHANNEL_URL, TELEGRAM_CHANNEL_URL } from '../../config/social';
import { FORM_GUIDE_URL } from '../../config/sisterSites';
import SiteLogo from '../common/SiteLogo';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-[#0c1630] via-[#091124] to-[#040813] text-slate-200 font-sans border-t-[2.5px] border-[#3b82f6] relative overflow-hidden shadow-[0_-8px_30px_rgba(0,0,0,0.35)]">
      {/* Illuminated Background Accent Glow Bubble */}
      <div 
        className="absolute -top-[140px] left-1/2 -translate-x-1/2 w-[800px] h-[340px] pointer-events-none opacity-90"
        style={{
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.18) 0%, rgba(12, 22, 48, 0) 70%)'
        }}
      />
      
      <div className="w-[96%] max-w-[1440px] mx-auto px-4 md:px-8 pt-9 pb-5 relative z-10">
        {/* 4-Column Clean Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1.1fr_1fr] gap-7 md:gap-8">
          
          {/* Column 1: Brand Info & Badges */}
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-3 mb-2.5">
              <Link to="/" className="inline-flex items-center justify-center p-0 shrink-0 hover:scale-105 transition-transform" aria-label="Sarkari Adda 24x7 Homepage">
                <SiteLogo
                  alt="Sarkari Adda 24x7"
                  loading="lazy"
                  className="h-12 md:h-[54px] w-auto max-h-[54px] object-contain flex-shrink-0 drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
                />
              </Link>
              <div className="flex flex-col gap-0.5">
                <Link to="/" className="font-heading text-[18px] md:text-[20px] font-bold tracking-tight leading-none inline-flex items-baseline text-left">
                  <span className="text-white mr-1.5">Sarkari</span>
                  <span className="text-[#38bdf8] inline-flex items-baseline">
                    Adda
                    <span className="text-[10px] md:text-[10.5px] font-bold text-[#f97316] align-baseline ml-1 leading-none">24x7</span>
                  </span>
                </Link>
                <div className="font-heading text-[11px] text-[#93c5fd] font-semibold leading-tight mt-0.5">
                  Govt Recruitment & Exam Portal
                </div>
              </div>
            </div>

            <p className="font-heading text-[11.5px] text-slate-300 leading-[1.65] mb-3 max-w-sm font-medium">
              India's trusted updates portal for all latest Sarkari Jobs, Admit Cards, Results, Answer Keys & Scholarships — directly verified from official gazettes & bulletins.
            </p>

            {/* Feature Badges */}
            <div className="flex items-center gap-1.5 flex-wrap w-full mt-0.5">
              <span className="font-heading text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <span className="text-emerald-400 font-extrabold text-[10px]">✓</span> Free Alerts
              </span>
              <span className="font-heading text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <span className="text-emerald-400 font-extrabold text-[10px]">✓</span> 100% Verified
              </span>
              <span className="font-heading text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <span className="text-emerald-400 font-extrabold text-[10px]">✓</span> Instant Updates
              </span>
            </div>
          </div>

          {/* Column 2: Portals & Alerts */}
          <div className="flex flex-col items-start">
            <h3 className="font-heading text-[12.5px] font-bold text-white uppercase tracking-[0.8px] mb-3 flex items-center">
              <span className="text-[#f97316] font-extrabold text-[15px] mr-1.5 leading-none">|</span>
              PORTALS & ALERTS
            </h3>
            <div className="flex flex-col w-full space-y-1.5 font-heading text-[11.5px] font-medium">
              <Link to="/jobs" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Latest Govt Jobs
              </Link>
              <Link to="/admit-cards" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Download Admit Cards
              </Link>
              <Link to="/results" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Sarkari Exam Results
              </Link>
              <Link to="/answer-key" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Official Answer Keys
              </Link>
              <Link to="/exams-calendar" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Exams Calendar
              </Link>
              <Link to="/scholarships" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Student Scholarships
              </Link>
              <Link to="/sarkari-yojana" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Sarkari Yojana
              </Link>
            </div>
          </div>

          {/* Column 3: Candidate Support */}
          <div className="flex flex-col items-start">
            <h3 className="font-heading text-[12.5px] font-bold text-white uppercase tracking-[0.8px] mb-3 flex items-center">
              <span className="text-[#f97316] font-extrabold text-[15px] mr-1.5 leading-none">|</span>
              CANDIDATE SUPPORT
            </h3>
            <div className="flex flex-col w-full space-y-1.5 font-heading text-[11.5px] font-medium">
              <Link to="/faq" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> FAQ & Help Center
              </Link>
              <Link to="/contact" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Contact Support
              </Link>
              <Link to="/about" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> About Sarkari Adda 24x7
              </Link>
              <Link to="/docs" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> API & Developer Docs
              </Link>
              <Link to="/smart-alerts" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Manage Smart Email Alerts
              </Link>
              {FORM_GUIDE_URL && (
                <a href={FORM_GUIDE_URL} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                  <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Sarkari Form Guide
                </a>
              )}
              <div className="pt-1.5">
                <Link to="/premium-plans" className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-600 hover:to-orange-600 text-white rounded font-heading font-bold text-[10.5px] transition-all shadow-2xs hover:scale-102">
                  <Crown className="w-3 h-3 text-amber-200" />
                  <span>Go Premium Plans</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Column 4: Legal & Community */}
          <div className="flex flex-col items-start">
            <h3 className="font-heading text-[12.5px] font-bold text-white uppercase tracking-[0.8px] mb-3 flex items-center">
              <span className="text-[#f97316] font-extrabold text-[15px] mr-1.5 leading-none">|</span>
              LEGAL & COMMUNITY
            </h3>
            <div className="flex flex-col w-full space-y-1.5 font-heading text-[11.5px] font-medium mb-3">
              <Link to="/privacy" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Terms of Service
              </Link>
              <Link to="/declaration" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> Disclaimer & Declaration
              </Link>
              <a href="/llms.txt" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white hover:translate-x-0.5 transition-all inline-flex items-center gap-1.5 group py-0.5">
                <span className="text-[#f97316] font-bold text-[13px] group-hover:translate-x-1 transition-transform">›</span> AI Agent Index (/llms.txt)
              </a>
            </div>

            {/* Glowing Social Icons (Sequence: WhatsApp, Instagram, Telegram, YouTube) */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <a
                href={WHATSAPP_CHANNEL_URL || 'https://whatsapp.com/channel'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#25d366] hover:border-[#25d366] hover:shadow-[0_0_12px_rgba(37,211,102,0.55)] transition-all hover:scale-110"
                aria-label="WhatsApp Channel"
                title="WhatsApp"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                </svg>
              </a>

              <a
                href="https://instagram.com/sarkari_adda"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#e4405f] hover:border-[#e4405f] hover:shadow-[0_0_12px_rgba(228,64,95,0.55)] transition-all hover:scale-110"
                aria-label="Instagram Page"
                title="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>

              <a
                href={TELEGRAM_CHANNEL_URL || 'https://t.me/sarkari_adda'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#0088cc] hover:border-[#0088cc] hover:shadow-[0_0_12px_rgba(0,136,204,0.55)] transition-all hover:scale-110"
                aria-label="Telegram Channel"
                title="Telegram"
              >
                <Send className="w-4 h-4" />
              </a>

              <a
                href="https://youtube.com/@sarkari_adda"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#ff0000] hover:border-[#ff0000] hover:shadow-[0_0_12px_rgba(255,0,0,0.55)] transition-all hover:scale-110"
                aria-label="YouTube Channel"
                title="YouTube"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>

            {/* Support Email */}
            <div>
              <a
                href="mailto:support@sarkariadda24x7.com"
                className="inline-flex items-center gap-1.5 font-heading text-[11.5px] text-[#60a5fa] hover:text-[#93c5fd] font-medium transition-colors"
              >
                <Mail className="w-3.5 h-3.5 text-[#60a5fa]" />
                <span>support@sarkariadda24x7.com</span>
              </a>
            </div>
          </div>

        </div>

        {/* Full-Width Clean Disclaimer */}
        <div className="mt-6 bg-[#0f172a] border border-[#f97316]/50 border-l-4 border-l-[#f97316] rounded-xl p-3 md:p-3.5 text-left shadow-xs">
          <p className="font-heading text-slate-300 text-[11.5px] leading-relaxed font-medium">
            <strong className="text-[#f97316] font-bold text-[12px]">Disclaimer:</strong> Sarkari Adda 24x7 is an independent informational portal and is not affiliated with, authorized, or endorsed by any government organization. All logos and trademarks belong to their respective owners.
          </p>
        </div>

        {/* Bottom Copyright Bar */}
        <div className="mt-4 pt-3 border-t border-slate-700/60 flex flex-col sm:flex-row items-center justify-between text-slate-400 font-heading text-[11.5px] font-medium gap-2">
          <span>© {new Date().getFullYear()} SarkariAdda24x7.com — All Rights Reserved</span>
          <div className="flex items-center gap-3 text-[11px] text-slate-400">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span>•</span>
            <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
            <span>•</span>
            <Link to="/declaration" className="hover:text-white transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
